import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import ModalImage from "react-modal-image";
import "./CandidateProjectMedia.scss";
import {
  createProjectMedia,
  fetchProjectMedia,
  updateProjectMedia,
  deleteProjectMedia,
} from "../../store/portfolio/action";
import { BASE_CANDIDATE_WORK_EXPERIENCE } from "@workspace/portfolio/src/helpers/url_helper";
import { CANDIDATE_WORK_EXPERIENCE_URL } from "@workspace/common/src/config";

function CandidateProjectMedia({ id, publicView }) {
  const dispatch = useDispatch();

  // Refs
  const projectFileInputRef = useRef(null);
  const updateProjectFileInputRef = useRef(null);

  // State
  const { projectMedia } = useSelector((state) => state?.PortfolioReducer);
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(fetchProjectMedia(id));
    }
  }, [id]);

  // Handle File Upload Click
  const handleFileUploadClick = () => {
    projectFileInputRef.current.click();
  };

  // Handle File Upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("workExperienceId", id);

    dispatch(createProjectMedia(formData));
  };

  // Set Update Input File
  const handleFileUpdateUploadClick = () => {
    updateProjectFileInputRef.current.click();
  };

  // Handle File Update Upload
  const handleFileUpdateUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    const payload = {
      id: projectMedia[id]?.id,
      formData: formData,
    };

    dispatch(updateProjectMedia(payload));
  };

  // Confirm Delete Modal for Project Media
  const confirmDelete = () => {
    setDeleteConfirmationModal(true);
  };

  // Handle Delete Project Media
  const handleDelete = () => {
    const mediaId = projectMedia[id]?.id;
    dispatch(deleteProjectMedia(mediaId));
    setDeleteConfirmationModal(false);
  };

  const renderMedia = () => {
    const media = projectMedia[id];
    // No media available
    if (!media?.id) {
      return publicView ? (
        <div className="p-3 project-media-empty">
          <div className="d-flex align-items-center justify-content-center gap-1 h-100">
            <span className="text-muted">Project media not available.</span>
          </div>
        </div>
      ) : (
        <div className="p-3 project-media-empty">
          <div className="d-flex flex-column align-items-center justify-content-center gap-1">
            <span className="fw-semibold">
              Click here to upload project media.
            </span>
            <span
              style={{ fontSize: "16px", color: "#3256ED" }}
              className="mdi mdi-progress-upload cursor-pointer"
              onClick={() => handleFileUploadClick(id)}
            ></span>
            <input
              type="file"
              ref={projectFileInputRef}
              onChange={handleFileUpload}
              style={{ display: "none" }}
            />
            <div className="d-flex flex-column align-items-center justify-content-center gap-1">
              <span className="fw-semibold">Instructions</span>
              <small className="fw-light">Video Format: MP4</small>
              <small className="fw-light">Image Format: JPG, JPEG or PNG</small>
            </div>
          </div>
        </div>
      );
    }

    // Media available
    const fileName = media?.fileName;
    const fileType = media?.fileType;
    const filePath = `${CANDIDATE_WORK_EXPERIENCE_URL}${BASE_CANDIDATE_WORK_EXPERIENCE}/project/${
      fileType === "mp4" ? "stream" : "view"
    }/${fileName}`;

    return (
      <div className="project-media-wrapper">
        {/* Overlay for edit/delete actions (hidden in public view) */}
        {!publicView && (
          <div className="project-media-overlay">
            <span
              className="mdi mdi-circle-edit-outline project-icon-mdi"
              onClick={handleFileUpdateUploadClick}
            ></span>
            <span
              className="mdi mdi-delete-outline project-icon-mdi"
              onClick={confirmDelete}
            ></span>
            <input
              type="file"
              ref={updateProjectFileInputRef}
              style={{ display: "none" }}
              onChange={handleFileUpdateUpload}
            />
          </div>
        )}

        {/* Render media based on file type */}
        {fileType === "mp4" ? (
          <video src={filePath} controls className="project-media" />
        ) : (
          <ModalImage
            small={filePath}
            large={filePath}
            hideDownload
            hideZoom
            className="project-media"
          />
        )}
      </div>
    );
  };

  return (
    <>
      {renderMedia()}
      <Modal
        isOpen={deleteConfirmationModal}
        toggle={() => setDeleteConfirmationModal(false)}
        centered
      >
        <ModalHeader toggle={() => setDeleteConfirmationModal(false)}>
          Confirm Delete Project Media
        </ModalHeader>
        <ModalBody>
          Are you sure you want to delete this project's media?
        </ModalBody>
        <ModalFooter>
          <div className="d-flex flex-row gap-3">
            <button
              className="btn btn-dark"
              onClick={() => setDeleteConfirmationModal(false)}
            >
              Cancel
            </button>
            <button className="btn btn-danger" onClick={handleDelete}>
              Delete
            </button>
          </div>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default CandidateProjectMedia;
